import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const Login = () => {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("signin"); // signin | reset

  if (session) return <Navigate to="/" replace />;

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Enter email and password");
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Signed in");
      navigate("/");
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Enter your email");
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Password reset email sent. Check your inbox.");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#193f7d]" style={{ fontFamily: "Outfit, sans-serif" }}>
            Gao Chill 五告凉
          </h1>
          <p className="text-sm text-slate-600 mt-2">Event P&L Tracker</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm">
          <h2 className="text-xl font-semibold text-slate-900 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
            {mode === "signin" ? "Sign in" : "Reset password"}
          </h2>
          <p className="text-sm text-slate-500 mb-6">
            {mode === "signin"
              ? "Access your Gao Chill workspace"
              : "We'll email you a reset link"}
          </p>

          <form onSubmit={mode === "signin" ? handleSignIn : handleReset} className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2 block">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-slate-50 border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-12 pl-10 pr-4 text-base"
                  placeholder="you@example.com"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            {mode === "signin" && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2 block">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-slate-50 border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-12 pl-10 pr-4 text-base"
                    placeholder="••••••••"
                    data-testid="login-password-input"
                  />
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#fa9d1c] hover:bg-[#e58a10] text-white rounded-full h-12 font-semibold transition-transform active:scale-95"
              data-testid={mode === "signin" ? "login-submit-btn" : "reset-submit-btn"}
            >
              {loading
                ? "Please wait..."
                : mode === "signin"
                ? "Sign in"
                : "Send reset email"}
            </Button>

            <button
              type="button"
              onClick={() => setMode(mode === "signin" ? "reset" : "signin")}
              className="w-full text-sm font-medium text-[#193f7d] hover:underline"
              data-testid="toggle-auth-mode-btn"
            >
              {mode === "signin" ? "Forgot password?" : "Back to sign in"}
            </button>
          </form>
        </div>

        <p className="text-xs text-slate-400 text-center mt-6">
          Internal use only. Contact the admin for account access.
        </p>
      </div>
    </div>
  );
};

export default Login;
